import {
  AccessorialCode,
  AccessorialCodeWellKnownNaturalId,
} from '@rygen/rygen-ui/model';

import i18n from '@/i18n';

import { RateQuote } from '../RateQuote';
import { RateQuoteAccessorial } from '../RateQuoteAccessorial';
import { RateQuoteChargeUpliftRuleUnit } from '../RateQuoteChargeUpliftRuleUnit';
import { RateUplift } from '../RateUplift';

import { EditQuoteClientChargeTableItem } from './EditQuoteClientChargeTableItem';
import { EditQuoteThreeplChargeTableItem } from './EditQuoteThreeplChargeTableItem';

// RateQuote -> Charge Table Items
export const toClientChargeTableItems = (
  rateQuote: RateQuote,
): EditQuoteClientChargeTableItem[] => {
  let indexKey = 0;

  const linehaulAccessorial = new AccessorialCode({
    name: i18n.global.t('shipments.financials.linehaul'),
    naturalId: AccessorialCodeWellKnownNaturalId.LINEHAUL,
  });

  const lineHaulChargeItem = new EditQuoteClientChargeTableItem({
    key: `${indexKey++}-${linehaulAccessorial.naturalId}`,
    chargeCode: linehaulAccessorial,
    rate: rateQuote.rawLineHaulNetCharge,
  });

  const fuelAccessorial = new AccessorialCode({
    name: i18n.global.t('shipments.financials.fuel'),
    naturalId: AccessorialCodeWellKnownNaturalId.FUEL,
  });

  const fuelChargeItem = new EditQuoteClientChargeTableItem({
    key: `${indexKey++}-${fuelAccessorial.naturalId}`,
    chargeCode: fuelAccessorial,
    rate: rateQuote.fuelSurchargeAmount,
  });

  const accessorialChargeItems: EditQuoteClientChargeTableItem[] =
    rateQuote.accessorials.map((accessorial) =>
      toClientAccessorialChargeTableItem(accessorial, indexKey++),
    );

  return [lineHaulChargeItem, fuelChargeItem, ...accessorialChargeItems];
};

export const toThreeplChargeTableItems = (
  rateQuote: RateQuote,
): EditQuoteThreeplChargeTableItem[] => {
  let indexKey = 0;

  const lineHaulAccessorial = new AccessorialCode({
    name: i18n.global.t('shipments.financials.linehaul'),
    naturalId: AccessorialCodeWellKnownNaturalId.LINEHAUL,
  });
  const lineHaulUpliftRateUnit = RateQuoteChargeUpliftRuleUnit.parse(
    rateQuote.upliftRule?.upliftRateUnit,
  );
  const lineHaulMarginRate =
    lineHaulUpliftRateUnit === 'FLAT'
      ? rateQuote.upliftAmount
      : rateQuote.upliftRule?.upliftRate;
  const lineHaulChargeItem = new EditQuoteThreeplChargeTableItem({
    key: `${indexKey++}-${lineHaulAccessorial.naturalId}`,
    chargeCode: lineHaulAccessorial,
    buyRate: rateQuote.rawLineHaulNetCharge,
    marginRate: lineHaulMarginRate,
    sellRate: rateQuote.lineHaulNetCharge,
    upliftUnit: lineHaulUpliftRateUnit,
  });

  const fuelAccessorial = new AccessorialCode({
    name: i18n.global.t('shipments.financials.fuel'),
    naturalId: AccessorialCodeWellKnownNaturalId.FUEL,
  });
  const fuelUpliftRateUnit = RateQuoteChargeUpliftRuleUnit.parse(
    rateQuote.fuelUpliftRule?.upliftRateUnit,
  );
  const fuelMarginRate =
    fuelUpliftRateUnit === 'FLAT'
      ? rateQuote.fuelUpliftAmount
      : rateQuote.fuelUpliftRule?.upliftRate;
  const fuelChargeItem = new EditQuoteThreeplChargeTableItem({
    key: `${indexKey++}-${fuelAccessorial.naturalId}`,
    chargeCode: fuelAccessorial,
    buyRate: rateQuote.fuelSurchargeAmount,
    marginRate: fuelMarginRate,
    sellRate: rateQuote.upliftedFuelAmount,
    upliftUnit: fuelUpliftRateUnit,
  });

  const accessorialChargeItems: EditQuoteThreeplChargeTableItem[] =
    rateQuote.accessorials.map((accessorial) =>
      toThreeplAccessorialChargeTableItem(accessorial, indexKey++),
    );

  return [lineHaulChargeItem, fuelChargeItem, ...accessorialChargeItems];
};

// RateQuoteAccessorial -> Charge Table Item
const toClientAccessorialChargeTableItem = (
  rateQuoteAccessorial: RateQuoteAccessorial,
  indexKey: number,
): EditQuoteClientChargeTableItem => {
  return new EditQuoteClientChargeTableItem({
    id: rateQuoteAccessorial.id,
    key: `${indexKey}-${rateQuoteAccessorial.chargeNaturalId}`,
    chargeCode: rateQuoteAccessorial.code,
    rate: rateQuoteAccessorial.charge,
    stopSequenceNumber: rateQuoteAccessorial.stopSequenceNumber,
  });
};

const toThreeplAccessorialChargeTableItem = (
  rateQuoteAccessorial: RateQuoteAccessorial,
  indexKey: number,
): EditQuoteThreeplChargeTableItem => {
  const marginRate =
    rateQuoteAccessorial.upliftRateUnit === 'FLAT'
      ? rateQuoteAccessorial.upliftAmount
      : rateQuoteAccessorial.upliftRate;

  return new EditQuoteThreeplChargeTableItem({
    id: rateQuoteAccessorial.id,
    key: `${indexKey}-${rateQuoteAccessorial.chargeNaturalId}`,
    chargeCode: rateQuoteAccessorial.code,
    buyRate: rateQuoteAccessorial.charge,
    marginRate: marginRate,
    sellRate: rateQuoteAccessorial.upliftedAmount,
    upliftUnit: rateQuoteAccessorial.upliftRateUnit,
    stopSequenceNumber: rateQuoteAccessorial.stopSequenceNumber,
  });
};

// Charge Table Items -> RateQuote
export const fromClientChargeTableItems = (
  rateQuote: RateQuote,
  chargeTableItems: EditQuoteClientChargeTableItem[],
): RateQuote => {
  const lineHaulChargeItem = chargeTableItems.find((item) =>
    item.isLineHaulCharge(),
  );

  if (!lineHaulChargeItem) {
    throw new Error('LineHaul Charge is required, but is missing');
  }

  const fuelChargeItem = chargeTableItems.find((chargeItem) =>
    chargeItem.isFuelCharge(),
  );

  if (!fuelChargeItem) {
    throw new Error('Fuel Charge is required, but is missing');
  }

  const accessorialChargeItems = chargeTableItems
    .filter(
      (chargeItem) =>
        !chargeItem.isFreightCharge() && chargeItem.hasChargeSelection,
    )
    .map((chargeItem) => {
      // ID match = original charge modified
      // No ID match = new charge
      // Original not ID matched = original charge removed
      const existing = rateQuote.accessorials.find((acc) =>
        chargeItem.matchesAccessorialById(acc.id),
      );

      return existing
        ? fromClientAccessorialChargeTableItem(existing, chargeItem)
        : chargeItem.toNewRateQuoteAccessorial();
    });

  const discount = rateQuote.discountAmount ?? 0;
  return new RateQuote({
    ...rateQuote,
    rawLineHaulNetCharge: lineHaulChargeItem.rate,
    rawLineHaulGrossCharge: lineHaulChargeItem.rate + discount,
    fuelSurchargeAmount: fuelChargeItem.rate,
    accessorials: accessorialChargeItems,
  });
};

export const fromThreeplChargeTableItems = (
  rateQuote: RateQuote,
  chargeTableItems: EditQuoteThreeplChargeTableItem[],
): RateQuote => {
  const lineHaulChargeItem = chargeTableItems.find((item) =>
    item.isLineHaulCharge(),
  );

  if (!lineHaulChargeItem) {
    throw new Error('LineHaul Charge is required, but is missing');
  }

  const fuelChargeItem = chargeTableItems.find((chargeItem) =>
    chargeItem.isFuelCharge(),
  );

  if (!fuelChargeItem) {
    throw new Error('Fuel Charge is required, but is missing');
  }

  const accessorialChargeItems = chargeTableItems
    .filter(
      (chargeItem) =>
        !chargeItem.isFreightCharge() && chargeItem.hasChargeSelection,
    )
    .map((chargeItem) => {
      const existing = rateQuote.accessorials.find((acc) =>
        chargeItem.matchesAccessorialById(acc.id),
      );

      return existing
        ? fromThreeplAccessorialChargeTableItem(existing, chargeItem)
        : chargeItem.toNewRateQuoteAccessorial();
    });

  const discount = rateQuote.discountAmount ?? 0;
  // uplifted (or Sell) rates (i.e. lineHaulNetCharge, upliftedFuelAmount, accessorial.upliftedAmount)
  // are computed in the backend and does not need to pass up User input
  return new RateQuote({
    ...rateQuote,
    rawLineHaulNetCharge: lineHaulChargeItem.buyRate,
    rawLineHaulGrossCharge: lineHaulChargeItem.buyRate + discount,
    upliftAmount: lineHaulChargeItem.upliftAmount,
    upliftRule: new RateUplift({
      upliftRate: lineHaulChargeItem.marginRate,
      upliftRateUnit: lineHaulChargeItem.upliftUnit,
    }),
    fuelSurchargeAmount: fuelChargeItem.buyRate,
    fuelUpliftAmount: fuelChargeItem.upliftAmount,
    fuelUpliftRule: new RateUplift({
      upliftRate: fuelChargeItem.marginRate,
      upliftRateUnit: fuelChargeItem.upliftUnit,
    }),
    accessorials: accessorialChargeItems,
  });
};

// Charge Table Item -> RateQuoteAccessorial
const fromClientAccessorialChargeTableItem = (
  rateQuoteAccessorial: RateQuoteAccessorial,
  chargeItem: EditQuoteClientChargeTableItem,
): RateQuoteAccessorial => {
  return new RateQuoteAccessorial({
    ...rateQuoteAccessorial,
    code: chargeItem.chargeCode,
    charge: chargeItem.rate,
    accessorialDescription: chargeItem.chargeCode?.name,
    stopSequenceNumber: chargeItem.stopSequenceNumber,
  });
};

const fromThreeplAccessorialChargeTableItem = (
  rateQuoteAccessorial: RateQuoteAccessorial,
  chargeItem: EditQuoteThreeplChargeTableItem,
): RateQuoteAccessorial => {
  return new RateQuoteAccessorial({
    ...rateQuoteAccessorial,
    code: chargeItem.chargeCode,
    charge: chargeItem.buyRate,
    accessorialDescription: chargeItem.chargeCode?.name,
    stopSequenceNumber: chargeItem.stopSequenceNumber,
    upliftAmount: chargeItem.upliftAmount,
    upliftRule: new RateUplift({
      upliftRate: chargeItem.marginRate,
      upliftRateUnit: chargeItem.upliftUnit,
    }),
  });
};
